import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { userService } from '@/services/user';
import { clientFormSchema, ClientFormData } from '../utils/clientFormSchema';

const formatWhatsapp = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return undefined;
  if (digits.startsWith('55')) return `+${digits}`;
  return `+55${digits}`;
};

interface AddClientModalProps {
  isOpen: boolean;
  partnerId: string;
  partnerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddClientModal({
  isOpen,
  partnerId,
  partnerName,
  onClose,
  onSuccess,
}: AddClientModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: { name: '', email: '', whatsapp: '' },
    mode: 'onChange',
  });

  const onSubmit = async (data: ClientFormData) => {
    setIsLoading(true);
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      await userService.createPartnerViewer({
        name: data.name.trim(),
        email: normalizedEmail,
        whatsapp: formatWhatsapp(data.whatsapp),
        partner_id: partnerId,
      });
      toast({
        title: 'Cliente adicionado com sucesso!',
        description: `O convite foi enviado para ${normalizedEmail}.`,
      });
      reset();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast({
          variant: 'error',
          title: 'Não foi possível cadastrar o cliente.',
          description: 'Este e-mail já está em uso na plataforma.',
        });
      } else {
        toast({
          variant: 'error',
          title: 'Não foi possível cadastrar o cliente.',
          description: 'Tente novamente ou entre em contato com o suporte.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle>Novo cliente — {partnerName}</DialogTitle>
          <DialogDescription>
            O cliente receberá um e-mail com um link para criar a senha e acessar os reports desta
            marca.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Nome *</label>
            <Input
              {...register('name')}
              placeholder="Digite o nome do cliente"
              className={errors.name ? 'border-red-500' : ''}
              error={errors.name?.message}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">E-mail *</label>
            <Input
              type="email"
              {...register('email')}
              placeholder="Digite o e-mail do cliente"
              className={errors.email ? 'border-red-500' : ''}
              error={errors.email?.message}
            />
            <p className="text-xs text-muted-foreground">
              O link de criação de senha será enviado para este e-mail.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">WhatsApp</label>
            <div className="flex gap-2">
              <div className="w-20 shrink-0 flex items-center justify-center rounded-md border border-input bg-muted px-3 text-sm text-gray-600">
                +55
              </div>
              <Input
                {...register('whatsapp')}
                placeholder="Digite o número do cliente"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Opcional. Usado para notificações de novos reports via WhatsApp.
            </p>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="dark" disabled={isLoading || !isValid}>
              {isLoading ? 'Enviando...' : 'Enviar convite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
